import { useMemo } from "react";

import type { NotificationRow as InboxStandardFragment } from "#app/lib/data";
import { getDateLabel, formatDateKey } from "@salestrak/ui";

export interface InboxDateGroup {
  /** YYYY-MM-DD key — matches the `date-group-${dateKey}` DOM id. */
  dateKey: string;
  /** Human label ("Today", "Yesterday", "Monday, June 3rd"). */
  dateLabel: string;
  items: InboxStandardFragment[];
}

export interface InboxDateGroups {
  groups: InboxDateGroup[];
  /** All days with items, sorted descending — feeds the jump-to-date popover.
   *  Derived from loaded items, so it grows as the feed paginates. */
  dateKeys: string[];
}

export function useInboxDateGroups(
  items: InboxStandardFragment[],
  ordering: "newest" | "oldest",
): InboxDateGroups {
  return useMemo(() => {
    // Group by local-day key so the keys line up with the popover's
    // today/yesterday math and the DOM ids the scroller jumps to.
    const map = new Map<string, InboxDateGroup>();

    for (const item of items) {
      const date = new Date(item.creationDate);
      const dateKey = formatDateKey(date);
      const group = map.get(dateKey);
      if (group) {
        group.items.push(item);
      } else {
        map.set(dateKey, {
          dateKey,
          dateLabel: getDateLabel(date),
          items: [item],
        });
      }
    }

    // dateKeys is always sorted descending regardless of display ordering, so
    // the popover's "earliest in range" math is correct either way.
    const dateKeys = [...map.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));

    // Sort groups by dateKey (same comparator as `dateKeys`) rather than
    // trusting Map insertion order: items arrive ordered by `updated_at DESC`,
    // not `creationDate`, so a read/cleared notification can show an old
    // creationDate before newer ones and break the day-header ordering.
    const groups = [...map.values()].sort((a, b) =>
      a.dateKey < b.dateKey ? 1 : a.dateKey > b.dateKey ? -1 : 0,
    );
    if (ordering === "oldest") groups.reverse();

    return { groups, dateKeys };
  }, [items, ordering]);
}
